import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { SETTINGS } from '../configs/common.settings';

export type SoftDeleteEntity =
  | 'lead'
  | 'quotation'
  | 'job'
  | 'additionalJob'
  | 'client'
  | 'invoice'
  | 'task';

export interface RevampFlags {
  pipelinePrimary: boolean;
  opportunityHub: boolean;
  softDeleteUi: boolean;
  calendarSlideout: boolean;
  newClientJobForms: boolean;
  departmentScoping: boolean;
  hideClassicSalesMenus: boolean;
  serverPermissions: boolean;
  /** Unified Billing V2 domain (additive migration). */
  billingV2: boolean;
}

/**
 * Advanced billing (Billing V2) ships in a later phase. While false, the
 * `billingV2` flag reads as off everywhere and all invoicing uses the legacy
 * `/app/invoice` screens, whatever the stored flag says.
 */
export const BILLING_V2_PHASE_ENABLED = false;

export const DEFAULT_REVAMP_FLAGS: RevampFlags = {
  pipelinePrimary: false,
  opportunityHub: false,
  softDeleteUi: false,
  calendarSlideout: false,
  newClientJobForms: false,
  departmentScoping: false,
  hideClassicSalesMenus: false,
  serverPermissions: true,
  billingV2: false,
};

export interface LinkSummaryItem {
  type: string;
  id: string;
  label: string;
}

export interface LinkSummaryResponse {
  entity: SoftDeleteEntity;
  id: string;
  links: LinkSummaryItem[];
  warning: string;
}

@Injectable({
  providedIn: 'root',
})
export class RevampService {
  private flagsCache$: Observable<RevampFlags> | null = null;
  private flagsSubject = new BehaviorSubject<RevampFlags>({
    ...DEFAULT_REVAMP_FLAGS,
  });

  /** Synchronous snapshot of last-loaded application flags. */
  readonly flags$ = this.flagsSubject.asObservable();

  constructor(private http: HttpClient) {}

  get flags(): RevampFlags {
    return this.flagsSubject.value;
  }

  isEnabled(flag: keyof RevampFlags): boolean {
    if (flag === 'billingV2' && !BILLING_V2_PHASE_ENABLED) return false;
    return !!this.flagsSubject.value[flag];
  }

  softDelete(entity: SoftDeleteEntity, ids: string[]): Observable<any> {
    return this.http.post(`${SETTINGS.BASE_API}/revamp/soft-delete`, {
      entity,
      ids,
    });
  }

  restore(entity: SoftDeleteEntity, ids: string[]): Observable<any> {
    return this.http.post(`${SETTINGS.BASE_API}/revamp/restore`, {
      entity,
      ids,
    });
  }

  linkSummary(
    entity: SoftDeleteEntity,
    id: string
  ): Observable<LinkSummaryResponse> {
    return this.http.get<LinkSummaryResponse>(
      `${SETTINGS.BASE_API}/revamp/link-summary/${entity}/${id}`
    );
  }

  /** Application-wide revamp flags (not company-scoped). */
  getFlags(_companyId?: string): Observable<RevampFlags> {
    if (!this.flagsCache$) {
      this.flagsCache$ = this.http
        .get<{ revamp: RevampFlags }>(`${SETTINGS.BASE_API}/revamp/flags`)
        .pipe(
          map(res => ({ ...DEFAULT_REVAMP_FLAGS, ...(res?.revamp || {}) })),
          tap(flags => this.flagsSubject.next(flags)),
          shareReplay({ bufferSize: 1, refCount: false })
        );
    }
    return this.flagsCache$;
  }

  /** Ensure application flags are loaded. */
  ensureFlagsLoaded(): Observable<RevampFlags> {
    return this.getFlags();
  }

  getHub(
    entityType: 'lead' | 'quotation' | 'job',
    id: string
  ): Observable<any> {
    return this.http.get(
      `${SETTINGS.BASE_API}/revamp/hub/${entityType}/${id}`
    );
  }

  getHubHistory(
    entityType: 'lead' | 'quotation' | 'job',
    id: string
  ): Observable<any> {
    return this.http.get(
      `${SETTINGS.BASE_API}/revamp/hub/${entityType}/${id}/history`
    );
  }

  getHubShifts(
    entityType: 'lead' | 'quotation' | 'job',
    id: string
  ): Observable<any> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http.get(
      `${SETTINGS.BASE_API}/revamp/hub/${entityType}/${id}/shifts`,
      { params: timezone ? { timezone } : {} }
    );
  }

  /**
   * Update application-wide flags (propagates to all companies on the server).
   * Signature keeps an optional unused companyId for call-site compatibility.
   */
  updateFlags(
    companyIdOrFlags: string | Partial<RevampFlags>,
    maybeFlags?: Partial<RevampFlags>
  ): Observable<RevampFlags> {
    const revamp: Partial<RevampFlags> =
      typeof companyIdOrFlags === 'string'
        ? maybeFlags || {}
        : companyIdOrFlags;
    this.clearFlagsCache();
    return this.http
      .post<{ revamp: RevampFlags }>(`${SETTINGS.BASE_API}/revamp/flags`, {
        revamp,
      })
      .pipe(
        map(res => ({ ...DEFAULT_REVAMP_FLAGS, ...(res?.revamp || {}) })),
        tap(flags => {
          this.flagsSubject.next(flags);
          this.flagsCache$ = of(flags).pipe(
            shareReplay({ bufferSize: 1, refCount: false })
          );
        })
      );
  }

  clearFlagsCache(_companyId?: string): void {
    this.flagsCache$ = null;
  }

  /** Hub detail path when opportunityHub is on; otherwise classic path. */
  detailPath(
    type: 'lead' | 'quotation' | 'job' | 'invoice',
    id: string
  ): string[] {
    if (type === 'invoice') {
      return this.invoiceDetailPath(id);
    }
    if (this.isEnabled('opportunityHub')) {
      return ['/app/pipeline', type, id];
    }
    return [`/app/${type}`, id];
  }

  /** Canonical invoice detail — Billing V2 when flag on, else classic invoice. */
  invoiceDetailPath(id: string): string[] {
    if (this.isEnabled('billingV2')) {
      return ['/app/billing-v2/invoices', id];
    }
    return ['/app/invoice', id];
  }

  /** Canonical invoice list / create entry points. */
  invoiceListPath(): string[] {
    return this.isEnabled('billingV2')
      ? ['/app/billing-v2']
      : ['/app/invoice'];
  }

  invoiceCreatePath(): string[] {
    return this.isEnabled('billingV2')
      ? ['/app/billing-v2/invoices/new']
      : ['/app/invoice/new'];
  }
}
